// ===================================
// TRENCH QUEST - Voice Mode Hook
// TTS narration + Speech-to-Text input
// Uses Web Speech API (free, browser built-in)
// ===================================

import { useState, useCallback, useEffect, useRef } from 'react'
import { useGameStore } from '@/store'

// Web Speech API type declarations
interface SpeechRecognitionEvent extends Event {
  resultIndex: number
  results: SpeechRecognitionResultList
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string
  message?: string
}

interface SpeechRecognitionResultList {
  length: number
  item(index: number): SpeechRecognitionResult
  [index: number]: SpeechRecognitionResult
}

interface SpeechRecognitionResult {
  isFinal: boolean
  length: number
  item(index: number): SpeechRecognitionAlternative
  [index: number]: SpeechRecognitionAlternative
}

interface SpeechRecognitionAlternative {
  transcript: string
  confidence: number
}

interface SpeechRecognitionInstance extends EventTarget {
  continuous: boolean
  interimResults: boolean
  lang: string
  onresult: ((event: SpeechRecognitionEvent) => void) | null
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null
  onend: (() => void) | null
  onstart: (() => void) | null
  start(): void
  stop(): void
  abort(): void
}

interface SpeechRecognitionConstructor {
  new (): SpeechRecognitionInstance
}

// Speech synthesis voice settings
const VOICE_SETTINGS = {
  rate: 1.1,      // Slightly faster than default
  pitch: 1.0,     // Normal pitch
  volume: 1.0,    // Full volume
}

// Preferred voice names (will try to match these)
const PREFERRED_VOICES = [
  'Google US English',
  'Google UK English Male',
  'Microsoft David',
  'Alex',        // macOS
  'Daniel',      // UK English
  'Samantha',    // macOS
]

interface UseVoiceModeReturn {
  // TTS
  isSpeaking: boolean
  speak: (text: string) => Promise<void>
  stopSpeaking: () => void

  // STT
  isListening: boolean
  transcript: string
  startListening: () => void
  startListeningForInput: () => void // Works regardless of voice mode - for mic button
  stopListening: () => void
  clearTranscript: () => void

  // State
  isVoiceModeEnabled: boolean
  isTTSSupported: boolean
  isSTTSupported: boolean
  error: string | null
}

export function useVoiceMode(): UseVoiceModeReturn {
  const voiceModeEnabled = useGameStore((state) => state.voiceModeEnabled)

  // TTS state
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [selectedVoice, setSelectedVoice] = useState<SpeechSynthesisVoice | null>(null)

  // STT state
  const [isListening, setIsListening] = useState(false)
  const [transcript, setTranscript] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Refs
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null)
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null)

  // Check browser support
  const isTTSSupported = typeof window !== 'undefined' && 'speechSynthesis' in window
  const isSTTSupported = typeof window !== 'undefined' && (
    'SpeechRecognition' in window || 'webkitSpeechRecognition' in window
  )

  // Load voices
  useEffect(() => {
    if (!isTTSSupported) return

    const loadVoices = () => {
      const voices = speechSynthesis.getVoices()
      if (voices.length === 0) return

      // Try to find a preferred voice
      let voice = voices.find(v =>
        PREFERRED_VOICES.some(pv => v.name.includes(pv))
      )

      // Fallback to first English voice
      if (!voice) {
        voice = voices.find(v => v.lang.startsWith('en'))
      }

      // Fallback to first available voice
      if (!voice) {
        voice = voices[0]
      }

      setSelectedVoice(voice ?? null)
    }

    loadVoices()
    speechSynthesis.addEventListener('voiceschanged', loadVoices)

    return () => {
      speechSynthesis.removeEventListener('voiceschanged', loadVoices)
    }
  }, [isTTSSupported])

  // Initialize speech recognition
  useEffect(() => {
    if (!isSTTSupported) return

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    const recognition = new SpeechRecognition()

    recognition.continuous = false
    recognition.interimResults = true
    recognition.lang = 'en-US'

    recognition.onresult = (event) => {
      let finalTranscript = ''
      let interimTranscript = ''

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]
        if (result) {
          const transcript = result[0]?.transcript || ''
          if (result.isFinal) {
            finalTranscript += transcript
          } else {
            interimTranscript += transcript
          }
        }
      }

      setTranscript(finalTranscript || interimTranscript)
    }

    recognition.onerror = (event) => {
      setError(`Speech recognition error: ${event.error}`)
      setIsListening(false)
    }

    recognition.onend = () => {
      setIsListening(false)
    }

    recognitionRef.current = recognition

    return () => {
      recognition.abort()
    }
  }, [isSTTSupported])

  // TTS: Speak text
  const speak = useCallback(async (text: string): Promise<void> => {
    if (!isTTSSupported || !voiceModeEnabled) return

    return new Promise((resolve, reject) => {
      // Cancel any ongoing speech
      speechSynthesis.cancel()

      // Clean text for speech (remove markdown, etc.)
      const cleanText = text
        .replace(/\*\*/g, '')        // Remove bold markers
        .replace(/\*/g, '')          // Remove italic markers
        .replace(/#{1,6}\s/g, '')    // Remove headers
        .replace(/\[.*?\]/g, '')     // Remove link text
        .replace(/\n+/g, '. ')       // Replace newlines with pauses
        .trim()

      const utterance = new SpeechSynthesisUtterance(cleanText)
      utteranceRef.current = utterance

      if (selectedVoice) {
        utterance.voice = selectedVoice
      }

      utterance.rate = VOICE_SETTINGS.rate
      utterance.pitch = VOICE_SETTINGS.pitch
      utterance.volume = VOICE_SETTINGS.volume

      utterance.onstart = () => {
        setIsSpeaking(true)
      }

      utterance.onend = () => {
        setIsSpeaking(false)
        resolve()
      }

      utterance.onerror = (event) => {
        setIsSpeaking(false)
        if (event.error !== 'interrupted') {
          reject(new Error(`Speech synthesis error: ${event.error}`))
        } else {
          resolve() // Interrupted is not an error
        }
      }

      speechSynthesis.speak(utterance)
    })
  }, [isTTSSupported, voiceModeEnabled, selectedVoice])

  // TTS: Stop speaking
  const stopSpeaking = useCallback(() => {
    if (isTTSSupported) {
      speechSynthesis.cancel()
      setIsSpeaking(false)
    }
  }, [isTTSSupported])

  // STT: Start listening (only if voice mode enabled - for auto-listen after TTS)
  const startListening = useCallback(() => {
    if (!isSTTSupported || !voiceModeEnabled || !recognitionRef.current) return

    setError(null)
    setTranscript('')

    try {
      recognitionRef.current.start()
      setIsListening(true)
    } catch (err) {
      setError('Failed to start speech recognition')
    }
  }, [isSTTSupported, voiceModeEnabled])

  // STT: Start listening for input (works regardless of voice mode - for mic button)
  const startListeningForInput = useCallback(() => {
    if (!isSTTSupported || !recognitionRef.current) return

    setError(null)
    setTranscript('')

    try {
      recognitionRef.current.start()
      setIsListening(true)
    } catch (err) {
      setError('Failed to start speech recognition')
    }
  }, [isSTTSupported])

  // STT: Stop listening
  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop()
      setIsListening(false)
    }
  }, [])

  // Clear transcript
  const clearTranscript = useCallback(() => {
    setTranscript('')
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isTTSSupported) {
        speechSynthesis.cancel()
      }
      if (recognitionRef.current) {
        recognitionRef.current.abort()
      }
    }
  }, [isTTSSupported])

  return {
    // TTS
    isSpeaking,
    speak,
    stopSpeaking,

    // STT
    isListening,
    transcript,
    startListening,
    startListeningForInput, // Works regardless of voice mode - for mic button in text input
    stopListening,
    clearTranscript,

    // State
    isVoiceModeEnabled: voiceModeEnabled,
    isTTSSupported,
    isSTTSupported,
    error,
  }
}

// Add type declarations for Web Speech API on window
declare global {
  interface Window {
    SpeechRecognition: SpeechRecognitionConstructor
    webkitSpeechRecognition: SpeechRecognitionConstructor
  }
}
